import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { trackEvent } from "@/lib/analytics";

const shot = (url) =>
  `https://image.thum.io/get/width/1400/crop/1000/noanimate/${url}`;

const projects = [
  {
    id: "01",
    year: "2026",
    title: "AI Evaluation Platform",
    subtitle: "Guardrails for production LLM systems",
    role: "Product Lead",
    cats: ["Evaluation", "LLM Ops", "Analytics"],
    metrics: [
      { k: "Eval throughput", v: "18×" },
      { k: "Time-to-signal", v: "−72%" },
      { k: "Model coverage", v: "6" },
    ],
    href: "https://aievalpilot.floot.app/",
    cover: "/projects/ai-eval.png",
    live: shot("https://aievalpilot.floot.app/"),
  },
  {
    id: "02",
    year: "2025",
    title: "AI Launchpad · Payments",
    subtitle: "Feature rollout system for a live payments app",
    role: "Product Manager, 0 → 1",
    cats: ["Feature Flags", "Beta Program", "Growth"],
    metrics: [
      { k: "Beta cohorts", v: "4" },
      { k: "Rollback safety", v: "<90s" },
      { k: "Feature velocity", v: "3.4×" },
    ],
    href: "https://ailaunchpad.floot.app/",
    cover: "/projects/ai-launchpad.png",
    live: shot("https://ailaunchpad.floot.app/"),
  },
  {
    id: "03",
    year: "2024",
    title: "Sparrow — Digital Wallet",
    subtitle: "Everyday money, engineered gently",
    role: "Founding Product Manager",
    cats: ["Wallet", "Onboarding", "Trust"],
    metrics: [
      { k: "D30 retention", v: "+41%" },
      { k: "KYC drop-off", v: "−58%" },
      { k: "NPS", v: "72" },
    ],
    href: "https://sparrow.floot.app/",
    cover: "/projects/sparrow-wallet.png",
    live: shot("https://sparrow.floot.app/"),
  },
  {
    id: "04",
    year: "2024",
    title: "MerchShield — Merchant Onboarding",
    subtitle: "Underwriting, at the speed of intent",
    role: "Product Manager",
    cats: ["Onboarding", "Risk", "Compliance"],
    metrics: [
      { k: "Time-to-live", v: "3d → 4h" },
      { k: "Fraud catch-rate", v: "+27%" },
      { k: "Merchants live", v: "12.4K" },
    ],
    href: "https://merchshield.floot.app/",
    cover: "/projects/merchshield.png",
    live: shot("https://merchshield.floot.app/"),
  },
];

const ProjectCard = ({ p, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8%" });
  const flip = i % 2 === 1;

  return (
    <motion.a
      ref={ref}
      href={p.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        trackEvent("project_click", {
          project_id: p.id,
          project_title: p.title,
          project_url: p.href,
        })
      }
      initial={{ opacity: 0, y: 70 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
      className="work-card group block rounded-2xl overflow-hidden border border-[var(--bone)]/10 bg-[var(--ink-2)]/50 backdrop-blur-sm hover:border-[var(--copper)]/40 transition-colors duration-500"
      data-testid={`project-${p.id}`}
    >
      <div className="grid grid-cols-12">
        {/* Text side */}
        <div
          className={`col-span-12 md:col-span-5 p-8 md:p-12 flex flex-col ${
            flip ? "md:order-2" : "md:order-1"
          }`}
        >
          <div className="flex items-center justify-between mb-8">
            <span className="chapter-num text-sm">P/{p.id}</span>
            <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--bone-dim)]">
              {p.year} · {p.role}
            </span>
          </div>

          <h3 className="font-serif text-3xl md:text-[2.9rem] leading-[1.0] text-[var(--bone)] group-hover:text-[var(--copper)] transition-colors duration-500">
            {p.title}
          </h3>
          <div className="mt-3 text-[var(--bone-dim)] text-base md:text-lg font-light italic">
            {p.subtitle}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {p.cats.map((c) => (
              <span
                key={c}
                className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--bone-dim)] border border-[var(--bone)]/15 rounded-full px-3 py-1"
              >
                {c}
              </span>
            ))}
          </div>

          <div className="mt-auto pt-10 grid grid-cols-3 gap-4">
            {p.metrics.map((m) => (
              <div key={m.k} className="border-l border-[var(--bone)]/15 pl-3">
                <div className="font-serif text-2xl md:text-3xl leading-none text-[var(--copper)]">
                  {m.v}
                </div>
                <div className="mt-1.5 font-mono text-[9px] tracking-[0.16em] uppercase text-[var(--bone-dim)]">
                  {m.k}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 inline-flex items-center gap-3">
            <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-[var(--copper)]">
              View case study
            </span>
            <span className="w-8 h-8 rounded-full border border-[var(--copper)] text-[var(--copper)] flex items-center justify-center group-hover:bg-[var(--copper)] group-hover:text-[var(--ink)] group-hover:rotate-45 transition-all duration-500">
              ↗
            </span>
          </div>
        </div>

        {/* Image side */}
        <div
          className={`col-span-12 md:col-span-7 relative overflow-hidden bg-[var(--ink-3)] min-h-[280px] md:min-h-[440px] ${
            flip ? "md:order-1" : "md:order-2"
          }`}
        >
          {/* Live screenshot (revealed on hover) */}
          <img
            src={p.live}
            alt={`${p.title} — live product screenshot`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-0 group-hover:opacity-100 transition-opacity duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            data-testid={`project-live-${p.id}`}
          />
          {/* Custom AI cover (default) */}
          <img
            src={p.cover}
            alt={`${p.title} — editorial cover`}
            className="absolute inset-0 w-full h-full object-cover object-center opacity-100 group-hover:opacity-0 group-hover:scale-[1.04] transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            data-testid={`project-image-${p.id}`}
          />
          {/* tint + edge fades for editorial feel */}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink-2)]/70 via-transparent to-[var(--ink-2)]/10 pointer-events-none" />
          <div
            className={`absolute inset-y-0 w-24 pointer-events-none ${
              flip
                ? "right-0 bg-gradient-to-l from-[var(--ink-2)]/80 to-transparent"
                : "left-0 bg-gradient-to-r from-[var(--ink-2)]/80 to-transparent"
            }`}
          />
          {/* corner ticks */}
          <div className="absolute top-4 left-4 w-4 h-4 border-t border-l border-[var(--copper)]/60" />
          <div className="absolute bottom-4 right-4 w-4 h-4 border-b border-r border-[var(--copper)]/60" />
          <span className="absolute bottom-4 left-5 font-mono text-[9px] tracking-[0.22em] uppercase text-[var(--bone)]/70 pointer-events-none">
            <span className="group-hover:opacity-0 transition-opacity duration-500">Cover · Editorial</span>
            <span className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 whitespace-nowrap">
              Live · {p.href.replace("https://", "").replace(/\/$/, "")}
            </span>
          </span>
        </div>
      </div>
    </motion.a>
  );
};

const Work = () => {
  return (
    <section id="work" className="relative py-28 md:py-40" data-testid="work-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="mb-16 md:mb-24 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--copper)] mb-4">
              [ Chapter III — Selected Work ]
            </div>
            <h2 className="font-serif text-6xl md:text-[8vw] leading-[0.9] text-[var(--bone)]">
              Four products,
              <br />
              <span className="italic text-[var(--bone-dim)]">one obsession.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 text-[var(--bone-dim)] text-base md:text-lg font-light leading-relaxed">
            Live projects — each one solves a real payment friction, from wallet
            onboarding to AI evaluation. Tap any case to explore the live build.
          </div>
        </div>

        <div className="flex flex-col gap-6 md:gap-8">
          {projects.map((p, i) => (
            <ProjectCard p={p} i={i} key={p.id} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Work;
